<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { inject, onMounted, ref, watch } from 'vue';
import { useInfinite } from '@/composables/use-infinity';

const backendApi = inject('backendApi');

const props = defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    itemTitle: {
        default: 'code'
    },
    itemValue: {
        default: 'id'
    },
    args: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update', 'update:focused']);

const selected = ref(null);
const previousAgencyId = ref(null);

const fetchLocations = ({ pattern, page, limit }) =>
    backendApi().locations().index({
        pattern,
        agency: props.args?.agency?.id,
        active: true,
        page,
        limit
    });

const {
    items: locations,
    loading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchLocations);

const handleSearch = () => {
    reset();
    getItems();
};

// Watch for changes to the agency ID specifically
watch(
    () => props.args?.agency?.id,
    (newAgencyId) => {
        // Only refresh if the agency ID has actually changed
        if (newAgencyId !== previousAgencyId.value) {
            previousAgencyId.value = newAgencyId;
            selected.value = null;
            emit('update', null); // Clear the selected value in parent
            handleSearch();
        }
    },
    {
        immediate: true
    }
);

onMounted(() => {
    getItems();
});
</script>

<template>
    <AutocompleteFilter
        v-model="selected"
        v-model:search="searchQuery"
        :item-title="itemTitle"
        :item-value="itemValue"
        :items="locations"
        :label="hideLabel ? '' : $t('Location')"
        :loading="loading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        @infinite-scroll="loadMore"
        @update="emit('update', selected)"
        @preparing-search="handleSearch"
        @search="handleSearch"
        @retry="handleSearch"
        @update:focused="(state) => emit('update:focused', state)"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.code}`"
                :subtitle="`${item.raw.description} ~ ${item.raw.zone?.name ?? ''}`"
            ></v-list-item>
        </template>
        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
    </AutocompleteFilter>
</template>
